import { useRef, useState, useCallback } from 'react';

/**
 * Lightweight HTML5 drag-and-drop reorder helper.
 *
 * Returns:
 *   reorderMode, enterReorder(), cancelReorder(), saveOrder(persistFn)
 *   orderDraft, setOrderDraft
 *   dragProps(itemId) → handlers to spread on a row
 *
 * Usage:
 *   const dnd = useReorder(items, (it) => it.id);
 *   const visible = dnd.reorderMode ? dnd.orderDraft.map(id => items.find(i=>i.id===id)) : items;
 *   <tr draggable={dnd.reorderMode} {...dnd.dragProps(item.id)} />
 */
export function useReorder(items, idAccessor = (x) => x.id) {
  const [reorderMode, setReorderMode] = useState(false);
  const [orderDraft, setOrderDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const dragIdRef = useRef(null);

  const enterReorder = useCallback(() => {
    setOrderDraft(items.map(idAccessor));
    setReorderMode(true);
  }, [items, idAccessor]);

  const cancelReorder = useCallback(() => {
    setReorderMode(false);
    setOrderDraft([]);
  }, []);

  const saveOrder = useCallback(async (persistFn) => {
    setSaving(true);
    try {
      await persistFn(orderDraft);
      setReorderMode(false);
      setOrderDraft([]);
    } finally {
      setSaving(false);
    }
  }, [orderDraft]);

  const dragProps = useCallback((id) => ({
    onDragStart: () => { dragIdRef.current = id; },
    onDragOver: (e) => e.preventDefault(),
    onDrop: () => {
      const src = dragIdRef.current;
      if (!src || src === id) return;
      setOrderDraft((prev) => {
        const next = prev.slice();
        const f = next.indexOf(src);
        const t = next.indexOf(id);
        if (f < 0 || t < 0) return prev;
        next.splice(f, 1);
        next.splice(t, 0, src);
        return next;
      });
      dragIdRef.current = null;
    },
  }), []);

  return { reorderMode, enterReorder, cancelReorder, saveOrder, saving, orderDraft, setOrderDraft, dragProps };
}
